import React, { FC, ReactElement } from 'react';
import { createFeature } from '@edreamsodigeo/frontend-core';
import {
  HighlightedType,
  highlightedTypesKeyMap,
} from '@components/Results/Components/ItineraryHeader/constants/highlightedTypes';
import { useTrackingInfo } from '../../../../../clientState/providers/SearchResponseProvider/index';
import ItineraryHeader from '@components/Results/Components/ItineraryHeader/index';
import { useItinerary } from '@components/Results/Components/Itinerary/Provider';
import {
  PERSONALISED_DECORATION,
  FLIGHT_OF_THE_DAY_DECORATION,
} from 'clientState/ab/aliases';

type HighlightDecoratorProps = {
  CardComponent: React.ElementType;
  type: string | null;
  highlightedType: string;
  sx: Record<string, unknown> | undefined;
  p?: number | string;
};

export const getPromotedItineraryTypes = (
  highlightedType: string,
  returningUserDeviceId: string | undefined,
  carbonFootprint: { isEco: boolean }
) => {
  const promotedItineraryTypes: string[] = [];

  if (highlightedType === 'recommended') {
    promotedItineraryTypes.push('ODI_RATING');
  }
  if (returningUserDeviceId) {
    promotedItineraryTypes.push('PERSONALIZED');
  }
  if (carbonFootprint && carbonFootprint.isEco) {
    promotedItineraryTypes.push('GREENER_CHOICE');
  }

  return promotedItineraryTypes;
};

const HighlightedDecoratorBase: FC<HighlightDecoratorProps> = ({
  CardComponent,
  type,
  children,
  sx,
  p,
  ...props
}) => {
  return (
    <CardComponent type={type} {...props} sx={sx} p={p}>
      {children}
    </CardComponent>
  );
};

const HighlightedDecoratorPersonalised: FC<HighlightDecoratorProps> = ({
  CardComponent,
  type,
  highlightedType,
  children,
  sx,
  p,
  ...props
}) => {
  const trackingInfo = useTrackingInfo();
  const { carbonFootprint = {} } = useItinerary();

  const promotedTypes = getPromotedItineraryTypes(
    highlightedType,
    trackingInfo?.returningUserDeviceId,
    carbonFootprint
  );

  let cardHeader: ReactElement | null = null;
  let cardType = type;

  if (type === 'positive') {
    let headerType: HighlightedType | undefined;

    if (promotedTypes.includes('PERSONALIZED')) {
      headerType = HighlightedType.Personalised;
      cardType = highlightedTypesKeyMap.personalised.type;
    } else if (promotedTypes.includes('ODI_RATING')) {
      headerType = HighlightedType.Recommended;
      cardType = highlightedTypesKeyMap.recommended.type;
    }
    cardHeader = (
      <ItineraryHeader highlightedType={headerType as HighlightedType} />
    );
  }

  return (
    <>
      {cardHeader}
      <CardComponent type={cardType} {...props} sx={sx} p={p}>
        {children}
      </CardComponent>
    </>
  );
};

const HighlightedDecoratorFlightOfTheDay: FC<HighlightDecoratorProps> = ({
  CardComponent,
  type,
  children,
  sx,
  p,
  ...props
}) => {
  let cardHeader: ReactElement | null = null;
  let cardType = type;

  const headerType = HighlightedType.FlightOfTheDay;
  cardType = highlightedTypesKeyMap.flightOfTheDay.type;

  cardHeader = (
    <ItineraryHeader highlightedType={headerType as HighlightedType} />
  );

  return (
    <>
      {cardHeader}
      <CardComponent
        type={cardType}
        {...props}
        sx={{ ...sx, position: 'relative' }}
        p={p}
      >
        {children}
      </CardComponent>
    </>
  );
};

const FlightOfTheDayDecoratorBase = createFeature({
  name: FLIGHT_OF_THE_DAY_DECORATION,
  variations: [
    HighlightedDecoratorBase,
    HighlightedDecoratorFlightOfTheDay,
    HighlightedDecoratorFlightOfTheDay,
  ],
  version: 1,
});

const HighlightedDecoratorPersonalisedBase = createFeature({
  name: PERSONALISED_DECORATION,
  variations: [HighlightedDecoratorBase, HighlightedDecoratorPersonalised],
  version: 1,
});

const HighlightedDecorator: FC<HighlightDecoratorProps> = (props) => {
  if (
    props.highlightedType === HighlightedType.Recommended ||
    props.highlightedType === HighlightedType.Personalised
  ) {
    return <HighlightedDecoratorPersonalisedBase {...props} />;
  }
  if (props.highlightedType === HighlightedType.FlightOfTheDay) {
    return <FlightOfTheDayDecoratorBase {...props} />;
  }
  return <HighlightedDecoratorBase {...props} />;
};

export default HighlightedDecorator;
